/**
 * Browser-side state for the two-factor login step.
 *
 * - Trusted devices live in localStorage under their own key, not inside the
 *   persisted `auth` store: logging out (and the 401 interceptor) removes
 *   `auth`, and a remembered device has to outlive both. Keyed by email so two
 *   accounts sharing a browser each keep their own token.
 * - The pending challenge lives in sessionStorage: it has to survive a refresh
 *   of the verify page, but not the tab.
 *
 * Every access is wrapped: storage can be missing or throw (private mode,
 * blocked site data), and the login must still work without it.
 */

const TRUSTED_DEVICES_KEY = 'eliio.trusted-devices';
const PENDING_CHALLENGE_KEY = 'eliio.two-factor-challenge';
const PENDING_PASSWORD_SETUP_KEY = 'eliio.password-setup';

type TrustedDevices = Record<string, string>;

interface PendingChallenge {
  challengeId: string;
  email: string;
  emailMasked: string;
  // Epoch ms when the current code was sent, for the resend countdown.
  sentAt: number;
  resendAfter: number;
}

const normalise = (email: string) => email.trim().toLowerCase();

const readTrustedDevices = (): TrustedDevices => {
  try {
    const raw = localStorage.getItem(TRUSTED_DEVICES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object'
      ? (parsed as TrustedDevices)
      : {};
  } catch {
    return {};
  }
};

const writeTrustedDevices = (devices: TrustedDevices) => {
  try {
    localStorage.setItem(TRUSTED_DEVICES_KEY, JSON.stringify(devices));
  } catch {
    // ignore storage errors (quota, private mode, etc.)
  }
};

const getTrustedDeviceToken = (email: string): string | undefined =>
  readTrustedDevices()[normalise(email)];

const saveTrustedDeviceToken = (email: string, token: string) => {
  writeTrustedDevices({ ...readTrustedDevices(), [normalise(email)]: token });
};

const removeTrustedDeviceToken = (email: string) => {
  const devices = readTrustedDevices();
  delete devices[normalise(email)];
  writeTrustedDevices(devices);
};

const getPendingChallenge = (): PendingChallenge | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_CHALLENGE_KEY);
    return raw ? (JSON.parse(raw) as PendingChallenge) : null;
  } catch {
    return null;
  }
};

const savePendingChallenge = (challenge: PendingChallenge) => {
  try {
    sessionStorage.setItem(PENDING_CHALLENGE_KEY, JSON.stringify(challenge));
  } catch {
    // ignore storage errors (quota, private mode, etc.)
  }
};

const clearPendingChallenge = () => {
  try {
    sessionStorage.removeItem(PENDING_CHALLENGE_KEY);
  } catch {
    // ignore storage errors (quota, private mode, etc.)
  }
};

/**
 * The one-time token from a login with the temporary password, kept so the
 * set-password page survives a refresh (sessionStorage, like the challenge).
 */
interface PendingPasswordSetup {
  token: string;
  email: string;
}

const getPendingPasswordSetup = (): PendingPasswordSetup | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_PASSWORD_SETUP_KEY);
    return raw ? (JSON.parse(raw) as PendingPasswordSetup) : null;
  } catch {
    return null;
  }
};

const savePendingPasswordSetup = (setup: PendingPasswordSetup) => {
  try {
    sessionStorage.setItem(PENDING_PASSWORD_SETUP_KEY, JSON.stringify(setup));
  } catch {
    // ignore storage errors (quota, private mode, etc.)
  }
};

const clearPendingPasswordSetup = () => {
  try {
    sessionStorage.removeItem(PENDING_PASSWORD_SETUP_KEY);
  } catch {
    // ignore storage errors (quota, private mode, etc.)
  }
};

// Recomputed from `sentAt` rather than counted down, so a refresh or a
// backgrounded tab still shows the real time left.
const secondsUntilResend = (code: { sentAt: number; resendAfter: number }) =>
  Math.max(
    0,
    Math.ceil((code.sentAt + code.resendAfter * 1000 - Date.now()) / 1000)
  );

export type { PendingChallenge, PendingPasswordSetup };
export {
  getTrustedDeviceToken,
  saveTrustedDeviceToken,
  removeTrustedDeviceToken,
  getPendingChallenge,
  savePendingChallenge,
  clearPendingChallenge,
  getPendingPasswordSetup,
  savePendingPasswordSetup,
  clearPendingPasswordSetup,
  secondsUntilResend,
};
